import { c as _c } from "react/compiler-runtime";
import { useContext } from 'react';
import useSWR from 'swr';
import { APIList } from '../../../../../../../typings/API.interface';
import { OutpostAttempt } from '../../../../../../../typings/outpost';
import APIMethodKeys from '../../../../client/APIMethodKeys';
import { capitalizeFirstLetter } from '../../../../utils';
import Badge from '../../../common/base/Badge';
import Button from '../../../common/base/Button';
import Chip from '../../../common/base/Chip';
import Icon from '../../../common/base/Icon';
import Skeleton from '../../../common/base/Skeleton';
import Text from '../../../common/base/Text';
import Tooltip from '../../../common/base/Tooltip';
import DisplayDate from '../../../common/DisplayDate';
import { EmptyResults, StyledEmptyStateWrapper } from '../../../common/EmptyState';
import { Div } from '../../../common/helpers/StyledUtils';
import JSONBrowser, { DynamicColumnHeader } from '../../../common/JSONBrowser';
import Table from '../../../common/Table/Table';
import { GlobalContext } from '../../../contexts/GlobalContext';
import { parseDynamicColumn, TableContext } from '../../../contexts/TableContext';
import { DestinationTypeIcon } from '../Destinations/DestinationTypeIcon';
import { getOutpostTenantLabel, useOutpostTenantsById } from '../utils/tenantDisplay';
type JSONPrimitive = string | number | boolean | null;
type JSONValue = JSONPrimitive | JSONValue[] | {
  [key: string]: JSONValue;
};
type AttemptField = string | number | boolean | JSX.Element | null;
type AttemptTableRow = {
  id: string;
  selected?: boolean;
  selectable?: boolean;
  highlighted?: boolean;
  expendable?: boolean;
  fields: AttemptField[];
  widths?: Array<{
    min?: number;
    max?: number;
  }>;
  element?: JSX.Element;
};
type AttemptNestedTable = {
  headers: Array<string | JSX.Element>;
  rows: AttemptTableRow[];
};
const getNestedValue = (obj: unknown, path: (string | number)[]): unknown => {
  if (path.length === 0) return obj;
  const [first, ...rest] = path;
  if (!obj || typeof obj !== 'object') return undefined;
  return getNestedValue((obj as Record<string, unknown>)[String(first)], rest);
};

// Module-scope helper: the try/catch lives here (outside the component) so the React
// Compiler can memoize AttemptsList — it bails on any component whose body contains a
// try/catch statement.
const renderDynamicColumnField = (column_string: string, attempt: OutpostAttempt) => {
  try {
    const parsed = parseDynamicColumn(column_string);
    let data: unknown;
    if (parsed.root === 'data') {
      data = attempt.event?.data || {};
    } else if (parsed.root === 'metadata') {
      data = attempt.event?.metadata || {};
    } else {
      return <Text key={`${attempt.id}-${column_string}`} muted size="xs">
          -
        </Text>;
    }
    const value = getNestedValue(data, parsed.path);
    const value_string = typeof value === 'object' ? JSON.stringify(value) : String(value);
    const json_value = value as JSONValue;
    if (value === undefined || value === null) {
      return <Text key={`${attempt.id}-${column_string}`} muted size="xs">
          -
        </Text>;
    }
    return <Tooltip key={`${attempt.id}-${column_string}`} side="bottom" fill disabled={value_string.length <= 25} delay={500} element={<Div style={{
      maxHeight: '300px',
      width: '300px',
      overflow: 'auto'
    }} p={{
      x: 1
    }}>
            <Text size="xs" semi muted m={{
        b: 1
      }}>
              Payload
            </Text>
            <JSONBrowser json={json_value} compact />
          </Div>}>
        <Text mono size="xs" ellipsis>
          {value_string}
        </Text>
      </Tooltip>;
  } catch {
    return <Text key={`${attempt.id}-${column_string}`} muted size="xs">
        -
      </Text>;
  }
};
const getContextTableConfigs = (): {
  outpost: {
    headers: string[];
    widths: Array<{
      min?: number;
      max?: number;
    }>;
  };
  'operator-events': {
    headers: string[];
    widths: Array<{
      min?: number;
      max?: number;
    }>;
  };
} => ({
  outpost: {
    headers: ['Time', 'Status', 'Topic', 'Tenant', 'Destination', 'Attempt'],
    widths: [{
      min: 180,
      max: 180
    }, {
      min: 124,
      max: 124
    }, {
      min: 180
    }, {
      min: 180
    }, {
      min: 180
    }, {
      min: 110,
      max: 110
    }]
  },
  'operator-events': {
    headers: ['Time', 'Status', 'Topic', 'Attempt'],
    widths: [{
      min: 180,
      max: 180
    }, {
      min: 124,
      max: 124
    }, {
      min: 180
    }, {
      min: 110,
      max: 110
    }]
  }
});
const DYNAMIC_COL_WIDTH = {
  min: 200,
  max: 200
};
const HISTORY_ATTEMPT_ROW_PREFIX = 'history:';
const AttemptsList: React.FC<{
  context?: 'outpost' | 'operator-events';
  attempts_response?: APIList<OutpostAttempt>;
  history_attempts_response?: APIList<OutpostAttempt>;
  loading: boolean;
  selected_attempt_id?: string;
  selected_history_attempt_id?: string;
  onAttemptSelected: (attempt_id: string | null) => void;
  onHistoryAttemptSelected?: (attempt_id: string) => void;
  onRetryAttempt?: (attempt: OutpostAttempt) => void;
  retrying_attempt_id?: string;
  onNextPage?: () => void;
  onPreviousPage?: () => void;
  current_page: number;
}> = t0 => {
  const $ = _c(29);
  const {
    attempts_response,
    history_attempts_response,
    loading,
    selected_attempt_id,
    selected_history_attempt_id,
    onAttemptSelected,
    onHistoryAttemptSelected,
    onRetryAttempt,
    retrying_attempt_id,
    context: t1,
    onNextPage,
    onPreviousPage,
    current_page
  } = t0;
  const context = t1 === undefined ? "outpost" : t1;
  const context_table_configs = getContextTableConfigs();
  const {
    dynamic_columns,
    reorderDynamicColumns,
    updateDynamicColumnWidth
  } = useContext(TableContext);
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  const attempts = attempts_response?.models || [];
  const tenant_ids = [...attempts.map(_temp), ...(history_attempts_response?.models || []).map(_temp2)];
  const {
    tenants_by_id
  } = useOutpostTenantsById(tenant_ids);
  const has_history = !!onHistoryAttemptSelected;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = APIMethodKeys.outpost.destinationTypes.list();
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  let t3;
  if ($[1] !== HookdeckAPI.outpost.destinationTypes) {
    t3 = () => HookdeckAPI.outpost.destinationTypes.list();
    $[1] = HookdeckAPI.outpost.destinationTypes;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const {
    data: destination_types
  } = useSWR(t2, t3);
  const widths = [...context_table_configs[context].widths, ...dynamic_columns.map(_temp3)];
  const headers = [...context_table_configs[context].headers, ...dynamic_columns.map(_temp4)];
  let t4;
  bb0: {
    if (dynamic_columns.length === 0) {
      let t5;
      if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
        t5 = [];
        $[3] = t5;
      } else {
        t5 = $[3];
      }
      t4 = t5;
      break bb0;
    }
    t4 = Array.from({
      length: dynamic_columns.length
    }, (_, i) => i + context_table_configs[context].headers.length);
  }
  const draggable_column_indices = t4;
  let t5;
  if ($[4] !== context || $[5] !== destination_types || $[6] !== dynamic_columns || $[7] !== onRetryAttempt || $[8] !== retrying_attempt_id || $[9] !== tenants_by_id) {
    t5 = attempt_1 => [<DisplayDate key={`${attempt_1.id}-time`} date={attempt_1.time} show_timezone={false} />, <Div key={`${attempt_1.id}-status`} flex={{
      gap: 2,
      justify: "space-between",
      align: "center"
    }}><Badge subtle={true} success={attempt_1.status === "success"} danger={attempt_1.status === "failed"}>{capitalizeFirstLetter(attempt_1.status)}{attempt_1.code ? ` (${attempt_1.code})` : ""}</Badge>{onRetryAttempt && <Button.Permission role="member" ghost={true} muted={true} small={true} icon={retrying_attempt_id === attempt_1.id ? "loading" : "retry"} disabled={retrying_attempt_id === attempt_1.id} onClick={event => {
        event.stopPropagation();
        onRetryAttempt(attempt_1);
      }} />}</Div>, <Text key={`${attempt_1.id}-topic`} mono={true} size="s" ellipsis={true}>{attempt_1.event?.topic || "-"}</Text>, ...(context === "outpost" ? [<Chip key={`${attempt_1.id}-tenant`} mono={true} outline={true} label={getOutpostTenantLabel(attempt_1.tenant_id, tenants_by_id[attempt_1.tenant_id])} to={`/tenants/${attempt_1.tenant_id}`} />, attempt_1.destination ? <Chip key={`${attempt_1.id}-destination`} outline={true} title={attempt_1.destination?.target} label={<Div flex={{
      align: "center",
      gap: 1
    }}><DestinationTypeIcon type={attempt_1.destination.type} type_meta={destination_types?.[attempt_1.destination.type]} /><Text as="span" size="xs" semi={true} p={{
        x: 1
      }} ellipsis={true}>{attempt_1.destination?.target}</Text></Div>} to={`/tenants/${attempt_1.tenant_id}/destinations/${attempt_1.destination_id}`} /> : <Text key={`${attempt_1.id}-destination`} muted={true} size="s" ellipsis={true}>{attempt_1.destination_id} (deleted)</Text>] : []), <Text key={`${attempt_1.id}-attempt`} size="s" flex={{
      gap: 1,
      align: "center"
    }}>{attempt_1.attempt_number}{attempt_1.manual && <Tooltip tooltip="Manual retry" align="left"><Icon icon="manual" neutral={true} m={{
          l: 2
        }} small={true} /></Tooltip>}</Text>, ...dynamic_columns.map(column_string_1 => renderDynamicColumnField(column_string_1, attempt_1))];
    $[4] = context;
    $[5] = destination_types;
    $[6] = dynamic_columns;
    $[7] = onRetryAttempt;
    $[8] = retrying_attempt_id;
    $[9] = tenants_by_id;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const getAttemptFields = t5;
  const current_rows = [];
  attempts.forEach(attempt_2 => {
    const is_selected_attempt = selected_attempt_id === attempt_2.id;
    const history_attempts = is_selected_attempt && history_attempts_response ? history_attempts_response.models : [];
    const other_history_attempts = history_attempts.filter(history_attempt => history_attempt.id !== attempt_2.id);
    current_rows.push({
      id: attempt_2.id,
      selected: is_selected_attempt && (!has_history || !selected_history_attempt_id),
      highlighted: is_selected_attempt && (!has_history || !selected_history_attempt_id),
      expendable: has_history,
      fields: getAttemptFields(attempt_2)
    });
    if (!has_history || !is_selected_attempt) {
      return;
    }
    if (!history_attempts_response) {
      current_rows.push({
        headers,
        rows: [{
          id: `${HISTORY_ATTEMPT_ROW_PREFIX}loading`,
          highlighted: true,
          selectable: false,
          fields: headers.map((__0, index) => <Skeleton key={`${attempt_2.id}-history-loading-${index}`} h={{
            px: 20
          }} w={{
            px: index < context_table_configs[context].headers.length ? 120 : 160
          }} />)
        }]
      });
      return;
    }
    if (other_history_attempts.length === 0) {
      current_rows.push({
        headers,
        rows: [{
          id: `${HISTORY_ATTEMPT_ROW_PREFIX}empty`,
          highlighted: true,
          selectable: false,
          widths: [{
            min: 100
          }],
          element: <Div p={{
            x: 6,
            y: 2
          }}><Text muted={true} size="s">No other attempts have been delivered to this destination for that event.</Text></Div>,
          fields: []
        }]
      });
      return;
    }
    current_rows.push({
      headers,
      rows: history_attempts.map(history_attempt_0 => ({
        id: `${HISTORY_ATTEMPT_ROW_PREFIX}${history_attempt_0.id}`,
        selected: selected_history_attempt_id === history_attempt_0.id,
        highlighted: selected_history_attempt_id === history_attempt_0.id,
        fields: getAttemptFields(history_attempt_0)
      }))
    });
  });
  const rows = current_rows;
  if (attempts.length === 0 && !loading) {
    let t6;
    if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <StyledEmptyStateWrapper><EmptyResults label="No matching attempts found" /></StyledEmptyStateWrapper>;
      $[11] = t6;
    } else {
      t6 = $[11];
    }
    return t6;
  }
  let t6;
  if ($[12] !== context || $[13] !== context_table_configs || $[14] !== reorderDynamicColumns) {
    t6 = (active_index, over_index) => {
      reorderDynamicColumns(active_index - context_table_configs[context].headers.length, over_index - context_table_configs[context].headers.length);
    };
    $[12] = context;
    $[13] = context_table_configs;
    $[14] = reorderDynamicColumns;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const handleColumnReorder = t6;
  let t7;
  if ($[16] !== context || $[17] !== context_table_configs || $[18] !== dynamic_columns || $[19] !== updateDynamicColumnWidth) {
    t7 = (visual_index, new_width) => {
      const dynamic_index = visual_index - context_table_configs[context].headers.length;
      if (dynamic_index >= 0 && dynamic_index < dynamic_columns.length) {
        const field_path = dynamic_columns[dynamic_index].split(":")[0];
        updateDynamicColumnWidth(field_path, new_width);
      }
    };
    $[16] = context;
    $[17] = context_table_configs;
    $[18] = dynamic_columns;
    $[19] = updateDynamicColumnWidth;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  const handleColumnResize = t7;
  let t8;
  if ($[21] !== has_history || $[22] !== onAttemptSelected || $[23] !== onHistoryAttemptSelected || $[24] !== selected_attempt_id || $[25] !== selected_history_attempt_id) {
    t8 = id => {
      if (!id) {
        onAttemptSelected(null);
        return;
      }
      if (id.startsWith(HISTORY_ATTEMPT_ROW_PREFIX)) {
        if (!onHistoryAttemptSelected) {
          return;
        }
        onHistoryAttemptSelected(id.replace(HISTORY_ATTEMPT_ROW_PREFIX, ""));
        return;
      }
      if (has_history && id === selected_attempt_id && selected_history_attempt_id) {
        onAttemptSelected(id);
        return;
      }
      onAttemptSelected(id === selected_attempt_id ? null : id);
    };
    $[21] = has_history;
    $[22] = onAttemptSelected;
    $[23] = onHistoryAttemptSelected;
    $[24] = selected_attempt_id;
    $[25] = selected_history_attempt_id;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  const handleRowSelected = t8;
  let t9;
  if ($[27] !== current_page) {
    t9 = {
      current_page,
      page_size: 50,
      entity_name: "attempts"
    };
    $[27] = current_page;
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  return <Table fill={true} footer={true} top_offset={0} headers={headers} widths={widths} rows={rows} loading={loading} loading_title="Loading attempts" loading_message="Loading attempts..." onRowSelected={handleRowSelected} draggable_columns={true} draggable_column_indices={draggable_column_indices} onColumnReorder={handleColumnReorder} onColumnResize={handleColumnResize} pagination={t9} onNextPage={onNextPage} onPreviousPage={onPreviousPage} current_count={attempts_response?.models.length} />;
};
export default AttemptsList;
function _temp(attempt) {
  return attempt.tenant_id;
}
function _temp2(attempt_0) {
  return attempt_0.tenant_id;
}
function _temp3(column_string) {
  const parsed = parseDynamicColumn(column_string);
  if (parsed.width) {
    return {
      min: parsed.width,
      max: parsed.width
    };
  }
  return DYNAMIC_COL_WIDTH;
}
function _temp4(column_string_0) {
  const parsed_0 = parseDynamicColumn(column_string_0);
  return <DynamicColumnHeader key={column_string_0} full_path={parsed_0.display_name} width={parsed_0.width} />;
}